import { useState } from 'react'
import type { MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'

//Utils
import { sanitizeText, sanitizeUrl } from '@/utils/sanitization'
import {
    formatSourceLinesHeadline,
    HEADLINE_LINE_LIMIT,
    isSelectedLine,
    parseSourceLines,
    sortSelectedLinesFirst,
    VISIBLE_LINE_LIMIT,
} from './sourceLines'
import useSelectedSourceLines from './useSelectedSourceLines'

//types
import type { MessageSourceProps } from './types/MessageSourceTypes'

//components
import { Accordion, Button, Link } from '@bosch/react-frok'
import CustomMarkdown from '../CustomMarkdown/CustomMarkdown'

import './MessageSource.scss'

export const MessageSource = ({ source, config }: MessageSourceProps) => {
    const { t } = useTranslation()
    const [showAllLines, setShowAllLines] = useState(false)
    const selectedLines = useSelectedSourceLines()
    const { link } = source
    const sanitizedLink = link && link !== 'undefined' && link !== 'None' ? sanitizeUrl(link) : null

    // A troubleshooting source can belong to many lines: the headline shortens them, the opened source lists them.
    // The lines of the line filter come first.
    const lines = sortSelectedLinesFirst(parseSourceLines(source.line), selectedLines)
    const hiddenLineCount = lines.length - VISIBLE_LINE_LIMIT
    const visibleLines = showAllLines ? lines : lines.slice(0, VISIBLE_LINE_LIMIT)

    const getLabel = (content: unknown, transform?: (v: string) => string): string => {
        if (content === 'undefined' || content === undefined) return 'N/A'
        const sanitized = typeof content === 'string' ? sanitizeText(content) : ''
        return transform ? transform(sanitized) : sanitized
    }

    const buildHeadline = (source: MessageSourceProps['source']): string => {
        return config
            .map((item) => {
                const content = item.key === 'line' ? formatSourceLinesHeadline(source[item.key], selectedLines) : source[item.key]
                const value = getLabel(content, item.transform)
                return item.prefix ? `${item.prefix}: ${value}` : value
            })
            .join(' | ')
    }

    return (
        <Accordion
            className="sources-accordion"
            size="small"
            headline={buildHeadline(source)}
        >
            {/* Up to HEADLINE_LINE_LIMIT lines are all in the headline already: only a shortened headline gets the list */}
            {lines.length > HEADLINE_LINE_LIMIT && (
                <div className="source-lines">
                    <span className="source-lines-title">{t('chat:filter:line:titlePlural')}</span>
                    <ul className="source-lines-list">
                        {visibleLines.map((line, index) => (
                            <li key={`${line}-${index}`}>
                                {/* The lines of the line filter are bold */}
                                {isSelectedLine(line, selectedLines) ? <strong>{sanitizeText(line)}</strong> : sanitizeText(line)}
                            </li>
                        ))}
                    </ul>
                    {hiddenLineCount > 0 && (
                        <Button
                            className="source-lines-toggle"
                            mode="integrated"
                            icon={{ iconName: showAllLines ? 'up' : 'down' }}
                            onClick={(event: MouseEvent) => {
                                // The source can be inside a solution card, which opens on a click
                                event.stopPropagation()
                                setShowAllLines((value) => !value)
                            }}
                        >
                            {showAllLines
                                ? t('chat:sourceLinesShowLess')
                                : `${t('chat:sourceLinesShowMore')} (${hiddenLineCount})`}
                        </Button>
                    )}
                </div>
            )}
            <CustomMarkdown className="sources-content">
                {getLabel(source.text)}
            </CustomMarkdown>
            {sanitizedLink && (
                <Link
                    className="source-link"
                    href={sanitizedLink}
                    target='_blank'
                    icon="externallink"
                    label={t('chat:sourceLink')}
                />
            )}
        </Accordion>
    )
}
